/* Where each room of the client portal sits.
 *
 * Twenty tabs in a sideways-scrolling strip asks the client to hunt. So each
 * feature has a placement: at the FRONT (a handful, always in view, no
 * scrolling), in the MENU (grouped, one click away), or OFF entirely.
 *
 * The default placement follows the project: what needs the client now is at the
 * front, and what's done — a signed engagement, an approved brief — moves out of
 * the way. The studio can override any of it. */

export type Placement = "front" | "menu" | "off";

/** The menu, grouped the way a client thinks about their project. */
export const NAV_GROUPS: { label: string; tabs: string[] }[] = [
  { label: "Your project", tabs: ["overview", "story", "progress", "reports"] },
  { label: "The design", tabs: ["film", "concept", "direction", "moodboard", "designs", "spaces"] },
  { label: "Drawings", tabs: ["drawings", "packages", "submissions", "reviews", "approvals"] },
  { label: "Tendering", tabs: ["tender"] },
  { label: "Scope & money", tabs: ["brief", "budget", "fees", "configure", "engagement", "billing"] },
  { label: "Delivery", tabs: ["rfis", "consultants"] },
  { label: "Talk to us", tabs: ["messages"] },
];

/** Rooms that belong to setting the project up, not running it. */
const SETUP = new Set(["brief", "moodboard", "direction", "configure", "engagement"]);

/** Always at the front — the client's way in and their way to reach us. */
const ALWAYS_FRONT = new Set(["overview", "messages"]);

function executing(p: any): boolean {
  const stages = p?.stages ?? [];
  const activeIdx = stages.findIndex((s: any) => s.status === "active");
  const curIdx = activeIdx >= 0 ? activeIdx : stages.length - 1;
  const docIdx = stages.findIndex((s: any) => s.key === "documentation");
  return docIdx >= 0 && curIdx >= docIdx;
}

/** True when this room is waiting on the client right now. Anything waiting on
 *  them belongs at the front, whatever else the rules say. */
function needsThem(p: any, key: string): boolean {
  switch (key) {
    case "brief": return p?.brief?.status === "sent";
    case "engagement": return p?.engagement?.status === "issued";
    case "concept": return p?.deck?.status === "issued" && (p.deck.options ?? []).length > 0
      && !(p.deck.options ?? []).some((o: any) => o.response?.choice === "preferred");
    case "designs": return (p?.presentation?.spaces ?? []).some((s: any) => s.response === "pending");
    case "approvals": return (p?.approvals ?? []).some((a: any) => a.status === "issued" || a.status === "pending");
    case "reviews": {
      for (const r of (p?.reviews ?? [])) { if (!r.sharedWithClient) continue;
        for (const s of r.sheets) for (const m of s.remarks) if (m.author === "client" && m.response && !m.response.accepted) return true; }
      return false;
    }
    case "rfis": return (p?.rfis ?? []).some((r: any) => r.status !== "closed" && r.ballInCourt === "client");
    case "billing": return (p?.invoices ?? []).some((i: any) => i.status === "sent" || i.status === "overdue");
    default: return false;
  }
}

/** What the project itself says about where a room belongs. */
export function defaultPlacement(p: any, key: string): Placement {
  if (ALWAYS_FRONT.has(key)) return "front";
  if (needsThem(p, key)) return "front";

  /* Only finished business goes into the menu. A room the studio has actually
     put something in belongs where the client can see it — the availability
     rules have already removed the empty ones, so the front row is only as long
     as the project is real. Hiding live work is worse than a longer row: the row
     wraps, and nobody scrolls sideways. */
  if (key === "engagement" && p?.engagement?.status === "signed") return "menu";
  if (key === "brief" && p?.brief?.status === "approved") return "menu";
  if (SETUP.has(key) && executing(p)) return "menu";
  /* Superseded once the concept is out: the mood board and the early directions
     did their job at the front end. */
  if ((key === "moodboard" || key === "direction") && p?.deck?.status === "issued") return "menu";
  /* A retrospective, not a thing to act on. */
  if (key === "story") return "menu";

  return "front";
}

/** Where a room actually sits, after the studio's curation. */
export function placementOf(p: any, key: string): Placement {
  if ((p?.portalHidden ?? []).includes(key)) return "off";
  if ((p?.portalPinned ?? []).includes(key)) return "front";
  if ((p?.portalTucked ?? []).includes(key)) return "menu";
  return defaultPlacement(p, key);
}

/** Split the rooms that exist on this project into the front row and the menu. */
export function splitNav<T extends { key: string }>(p: any, available: T[]): { front: T[]; groups: { label: string; tabs: T[] }[] } {
  const byKey = new Map(available.map((t) => [t.key, t]));
  const front = available.filter((t) => placementOf(p, t.key) === "front");
  const inMenu = available.filter((t) => placementOf(p, t.key) === "menu");
  const groups = NAV_GROUPS
    .map((g) => ({ label: g.label, tabs: g.tabs.map((k) => byKey.get(k)).filter((t): t is T => !!t && inMenu.includes(t)) }))
    .filter((g) => g.tabs.length > 0);
  /* Anything the groups don't know about still has to be reachable. */
  const grouped = new Set(groups.flatMap((g) => g.tabs.map((t) => t.key)));
  const rest = inMenu.filter((t) => !grouped.has(t.key));
  if (rest.length) groups.push({ label: "Everything else", tabs: rest });
  return { front, groups };
}
